import { NgModule, Version, ErrorHandler } from "@angular/core";
import { APP_BASE_HREF } from "@angular/common";
import { BrowserModule, Title } from "@angular/platform-browser";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { ReactiveFormsModule, Validators } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import * as Sentry from "@sentry/angular";
import { MarkdownModule } from "ngx-markdown";
import { PlanungApiModule } from "./planung-api/planung-api.module";
import { InfoportalApiModule } from "./infoportal-api/infoportal-api.module";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { LoginComponent } from "./login/login.component";
import { AlertService } from "./services";
import { AuthGuard } from "./guards";
import { CommonComponentsModule } from "./common-components";
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from "@angular/cdk/overlay";

import { CstDialogsModule } from "./common-components/dialogs/cst-dialogs.module";
import { DIALOG_MAX_HEIGHT_TOKEN } from "./injection-tokens/dialog-max-height-token";
import { AppVersion } from "./app-version";
import { APP_TITLE_TOKEN } from "./injection-tokens/app-title-token";
import { WaWiApiModule } from "./wawi-api/wawi-api.module";
import { CstPasswordModule } from "./password/password.module";
import {
  CstValidatorEmail,
  CstValidatorRequiredAndNonEmpty,
} from "./helpers/CstValidators";
import { APP_VERSION } from "./administration/versions/app-version.token";
import { AnlageApiComponentsModule } from "./anlage-api/components/anlage-components.module";
import { WebApiModule } from "./web-api/web-api.module";
import { CstGeneralApiModule } from "./general-api/general.module";
import { VertragApiModule } from "./vertrag-api/vertrag-api.module";
import { ClarionLoginComponent } from "./login/clarion_login/clarion_login.component";
import { Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { AuthRefreshInterceptor, ClientTimezoneInterceptor, ErrorInterceptor, LanguageInterceptor } from "./http-interceptors";
import { provideBackendFeatureFlags } from "./injection-tokens/backend-feature-flags";
import { CstResponsivePopupModule } from "./common-components/resp-popup/resp-popup.module";
import { TermsOfServicePopupComponent } from "./login/tos-popup-component/tos-popup.component";
import { providePrimeNG } from 'primeng/config';
import Material from '@primeuix/themes/material';

// Den eingebauten E-Mail Validator überschreiben, da er auch Mails für lokale Domain-Namen zulässt z.B. "foo@bar",
// was der Validator auf der Server-Seite nicht macht, deshalb wird dieser hier angepasst.
Validators.email = CstValidatorEmail;
Validators.required = CstValidatorRequiredAndNonEmpty;

function dateToJson(): string {
  const fourDigOptions = { minimumIntegerDigits: 4, useGrouping: false };
  const twoDigOptions = { minimumIntegerDigits: 2, useGrouping: false };

  const year = this.getFullYear().toLocaleString(undefined, fourDigOptions);
  const month = (this.getMonth() + 1).toLocaleString(undefined, twoDigOptions);
  const day = this.getDate().toLocaleString(undefined, twoDigOptions);

  const hours = this.getHours().toLocaleString(undefined, twoDigOptions);
  const minutes = this.getMinutes().toLocaleString(undefined, twoDigOptions);
  const seconds = this.getSeconds().toLocaleString(undefined, twoDigOptions);

  const offset = this.getTimezoneOffset();
  const tzsign = offset < 0 ? "+" : "-";
  const tzhours = Math.round(Math.abs(offset) / 60).toLocaleString(
    undefined,
    twoDigOptions,
  );
  const tzminutes = Math.round(Math.abs(offset) % 60).toLocaleString(
    undefined,
    twoDigOptions,
  );
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzsign}${tzhours}:${tzminutes}`;
}

@NgModule({
  declarations: [
    AppComponent,
    ClarionLoginComponent,
    TermsOfServicePopupComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    LoginComponent,
    CstGeneralApiModule,
    MarkdownModule.forRoot(),
    AnlageApiComponentsModule,
    PlanungApiModule,
    WaWiApiModule,
    VertragApiModule,
    InfoportalApiModule,
    ReactiveFormsModule,
    CommonComponentsModule,
    CstDialogsModule,
    CstPasswordModule,
    WebApiModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    CstResponsivePopupModule,
  ],
  providers: [
    AlertService,
    AuthGuard,
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    { provide: DIALOG_MAX_HEIGHT_TOKEN, useValue: "calc(100% - 80px)" },
    { provide: APP_VERSION, useValue: new Version(AppVersion.full) },
    { provide: HTTP_INTERCEPTORS, useClass: ClientTimezoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthRefreshInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: APP_BASE_HREF,
      useFactory: () => {
        let pre = "";
        if (
          document.location.href.endsWith("/test") ||
          document.location.href.includes("/test/")
        ) {
          pre = "/test";
        }
        return (
          pre + document.getElementsByTagName("base")[0].getAttribute("href")
        );
      },
    },
    {
      provide: APP_TITLE_TOKEN,
      useFactory: (appTitleService: Title) => {
        let result = $localize`:@@AppTitle:MainHero - LiftSuite`;
        if (document.location.href.includes("/test")) {
          result += " " + $localize`:@@AppTitle_TestEnvironment:(Test Environment)`;
        }
        appTitleService.setTitle(result);
        return result;
      },
      deps: [Title],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    ...provideBackendFeatureFlags,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: Material,
        options: {
          darkModeSelector: ".dark-theme",
        }
      }
    }),
  ],
})
export class AppModule {
  constructor() {
    Date.prototype.toJSON = dateToJson;
    Date.prototype.toISOString = dateToJson;
  }
}
